import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { FilterPill } from '@/components/common/FilterPill';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import { usePayments } from '@/data/api';
import { formatDateTime, formatKes, maskAccount } from '@/lib/format';
import { Modal } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { Payment } from '@/types/request';

const STATUS_COLOR: Record<Payment['status'], string> = {
  SUCCESS: 'bg-status-success-bg text-status-success',
  FAILED: 'bg-status-error-bg text-status-error',
  PENDING: 'bg-status-warning-bg text-status-warning-text',
  CANCELLED: 'bg-line-soft text-ink-muted',
};

const METHOD_LABEL: Record<Payment['method'], string> = {
  MPESA: 'M-Pesa',
  CARD: 'Card',
  BANK_TRANSFER: 'Bank transfer',
};

const PAGE_SIZE = 20;

export function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Payment['status'][]>([]);
  const [methodFilter, setMethodFilter] = useState<Payment['method'][]>([]);
  const [selected, setSelected] = useState<Payment | null>(null);
  const [page, setPage] = useState(0);

  // Snap back to page 0 when the user changes filters so we don't render an
  // empty page off the end of a now-smaller result set.
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, methodFilter]);

  // Method filter stays client-side for now — the legacy mpesa_c2btransactions
  // table only carries M-Pesa records, so it's a no-op until card / bank
  // transfer sources are wired. The status filter is sent server-side
  // because that's much cheaper than re-pulling everything.
  const listQuery = usePayments({
    search,
    status: statusFilter.length === 1 ? statusFilter[0] : undefined,
    page,
    size: PAGE_SIZE,
  });
  const filtered = (listQuery.data?.content ?? []).filter((p) => {
    if (statusFilter.length > 1 && !statusFilter.includes(p.status)) return false;
    if (methodFilter.length && !methodFilter.includes(p.method)) return false;
    return true;
  });
  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <Card padding="sm" className="flex flex-wrap items-center gap-2.5">
        <Input
          containerClassName="w-72"
          placeholder="Search by ref, customer, account"
          iconLeft={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {(['SUCCESS', 'FAILED', 'PENDING', 'CANCELLED'] as const).map((s) => (
          <FilterPill
            key={s}
            label={s.charAt(0) + s.slice(1).toLowerCase()}
            active={statusFilter.includes(s)}
            onClick={() => {
              setStatusFilter((prev) =>
                prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
              );
            }}
            onClear={() => setStatusFilter(statusFilter.filter((x) => x !== s))}
          />
        ))}
        <div className="w-px h-6 bg-line mx-1" />
        {(['MPESA', 'CARD', 'BANK_TRANSFER'] as const).map((m) => (
          <FilterPill
            key={m}
            label={METHOD_LABEL[m]}
            active={methodFilter.includes(m)}
            onClick={() => {
              setMethodFilter((prev) =>
                prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
              );
            }}
            onClear={() => setMethodFilter(methodFilter.filter((x) => x !== m))}
          />
        ))}
      </Card>

      <Card padding="none" className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : listQuery.isError ? (
          <ErrorState
            title="Couldn't load payments"
            description={(listQuery.error as Error)?.message ?? 'Try again.'}
            onRetry={() => listQuery.refetch()}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No payments match your filters"
            description="Adjust the search or status filter."
          />
        ) : (
        <table className="w-full text-body">
          <thead className="border-b border-line">
            <tr className="text-overline text-ink-hint uppercase">
              <th className="text-left px-4 py-2.5">Ref</th>
              <th className="text-left py-2.5">Customer</th>
              <th className="text-left py-2.5">Account</th>
              <th className="text-left py-2.5">Method</th>
              <th className="text-right py-2.5">Amount</th>
              <th className="text-left py-2.5 pl-6">Status</th>
              <th className="text-left px-4 py-2.5">Initiated</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr
                key={p.id}
                onClick={() => setSelected(p)}
                className="border-t border-line-soft cursor-pointer hover:bg-canvas-hover transition-colors"
              >
                <td className="px-4 py-2.5 font-mono text-caption">{p.referenceNumber}</td>
                <td className="py-2.5">{p.customer.name}</td>
                <td className="py-2.5 font-mono">{maskAccount(p.accountNumber)}</td>
                <td className="py-2.5">{METHOD_LABEL[p.method]}</td>
                <td className="py-2.5 text-right font-mono tabular">KES {formatKes(p.amount)}</td>
                <td className="py-2.5 pl-6">
                  <span className={cn(
                    'inline-flex items-center rounded-pill px-2.5 py-0.5 text-overline uppercase',
                    STATUS_COLOR[p.status],
                  )}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-ink-muted">{formatDateTime(p.initiatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
        {!listQuery.isLoading && !listQuery.isError && totalElements > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Card>

      <Modal
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        title={selected ? `Payment ${selected.referenceNumber}` : 'Payment'}
        description={selected ? METHOD_LABEL[selected.method] : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
            <Button variant="primary">Resend receipt</Button>
          </>
        }
      >
        {selected && (
          <div className="grid grid-cols-2 gap-4 text-body">
            <Field label="Customer" value={selected.customer.name} />
            <Field label="Account" value={selected.accountNumber} mono />
            <Field label="Amount" value={`KES ${formatKes(selected.amount)}`} mono />
            <Field label="Method" value={METHOD_LABEL[selected.method]} />
            <Field label="Status" value={selected.status} />
            <Field label="Initiated" value={formatDateTime(selected.initiatedAt)} />
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-caption text-ink-muted">{label}</div>
      <div className={cn('text-body text-ink', mono && 'font-mono')}>{value}</div>
    </div>
  );
}
